<page-title [value]="workstationName || workstation.hostname" />
<h3 class="page-title">
  <a class="segment arrow" [routerLink]="['/', 'workstation']" translate>Workstations</a>
  <span class="segment">{{ workstationName || workstation.hostname }}</span>
</h3>
<div class="content-box" [formGroup]="form">
  <div class="row">
    <div class="col-12 col-md-6">
      @if (clientSessions.length) {
        <h3 translate>Active sessions</h3>
      }

      @for (session of clientSessions; track session) {
        <div class="mb-4">
          <div class="d-flex">
            <avatar class="me-3" [user]="session.user" />
            <div class="w-100">
              <div data-test-qa="userDisplayName">{{ session.user?.displayName }}</div>
              <badge scheme="success"><span translate>Logged in</span></badge>
            </div>
          </div>
          <div class="list-group list-group-light mt-3">
            @for (mw of session.mountedWorkspaces; track mw) {
              <a
                class="list-group-item list-group-item-action no-wrap d-flex"
                [routerLink]="['/', 'workspace', mw.workspace.id]"
                data-test-qa="mountedWorkspace"
              >
                <i class="far fa-folder fa-2x me-3"></i>
                <div class="w-100">
                  <div class="d-flex">
                    <div>{{ mw.workspace.production.name }} {{ mw.workspace.name }}</div>
                    @if (mw.mountNode) {
                      <div class="text-muted ms-auto">
                        <i class="fas fa-hdd icon me-1"></i>
                        <span>{{ mw.mountNode.name }}</span>
                      </div>
                    }
                  </div>
                  <div class="d-flex text-muted">
                    <div>{{ mw.mountpoint }}</div>
                    <div class="ms-auto"></div>
                    @if (mw.mountAddress) {
                      <div>{{ mw.mountAddress.address }} /</div>
                    }
                    <div class="ms-1">{{ getProtocolName(mw.protocol) }}</div>
                  </div>
                </div>
              </a>
            }
          </div>
        </div>
      } @empty {
        <empty-state
          [text]="'No active sessions' | translate"
          icon="empty-set"
          data-test-qa="noClientSessionsLabel"
        />
      }
      @if (workstation.report?.version) {
        <div data-test-qa="report">
          <h3 translate>Report</h3>
          <div class="card bg-dark mb-3">
            <div class="card-body d-flex align-items-center pt-3 pb-3">
              <h4 class="m-0 me-auto">{{ workstation.report.os.type }}</h4>
              <div class="text-right selectable">
                {{ workstation.report.os.release }}
                @if (workstation.report.os.build) {
                  <span class="ms-2 text-muted">({{ workstation.report.os.build }})</span>
                }
              </div>
            </div>
          </div>
          <div class="card bg-dark mb-3">
            <div class="card-body d-flex align-items-center pt-3 pb-3">
              <h4 class="m-0 me-auto">ELEMENTS Client</h4>
              <div class="text-right selectable">{{ workstation.report.client.version }}</div>
            </div>
          </div>
          <h5 class="mt-4 mb-3" translate>Network interfaces</h5>
          @for (interface of workstation.report.interfaces | keyvalue; track interface) {
            <div class="card bg-dark mb-3">
              <div class="card-body d-flex align-items-center pt-3 pb-3">
                <h4 class="m-0 me-auto">{{ interface.key }}</h4>
                <div class="text-right">
                  @for (address of $any(interface.value); track address) {
                    <code class="d-block selectable">
                      {{ address.cidr }}
                    </code>
                  }
                </div>
              </div>
            </div>
          }
          <h5 class="mt-4 mb-3" translate>Checks</h5>
          @for (check of workstation.report.checks; track check) {
            <div class="card bg-dark mb-3">
              <div class="card-body d-flex align-items-center pt-3 pb-3">
                <h4 class="m-0 me-auto">{{ check.name }}</h4>
                @if (check.success === false) {
                  <badge scheme="warning">{{ check.label }}</badge>
                }
                @if (check.success === true) {
                  <badge scheme="success">{{ check.label }}</badge>
                }
              </div>
              @if (check.success === false) {
                <div class="card-footer text-muted">
                  {{ check.message }}
                </div>
              }
            </div>
          }
        </div>
      }
    </div>
    <div class="col-12 col-md-6">
      <div class="form-line">
        <div class="header">
          <div class="title" translate>Hostname</div>
        </div>
        <div class="form-control-static selectable">{{ workstation.hostname }}</div>
      </div>
      <div class="form-line">
        <div class="header">
          <div class="title" translate>Display name</div>
        </div>
        <input
          class="form-control"
          type="text"
          name="workstationName"
          formControlName="workstationName"
          [placeholder]="'None' | translate"
          data-test-qa="workstationName"
          permissionEnable="workstations:manage"
        />
      </div>
      <div class="form-line">
        <div class="header">
          <div class="title">
            <span translate>Last seen</span>
            <new-badge version="3.6" />
          </div>
        </div>
        <div class="last-seen">
          @if (workstation.lastSeen) {
            <div class="form-control-static selectable">
              {{ workstation.lastSeen | utc | timeAgo }}
            </div>
          }
          @if (!workstation.lastSeen) {
            <div class="form-control-static selectable" translate>Never</div>
          }
        </div>
      </div>
    </div>
  </div>
</div>
@if (workstation) {
  <floating-toolbar permissionShow="workstations:manage">
    <save-button [click]="save" [disabled]="!form.valid" />
    <div class="me-auto"></div>
    <button class="btn btn-outline-danger" (click)="delete()" data-test-qa="delete">
      <i class="far fa-trash-alt"></i>
      <span translate>Delete</span>
    </button>
  </floating-toolbar>
}
